import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { validateEmail, validatePhone, sanitizeText, validatePassword } from "../utils/validator";
import { useLoading } from "../contexts/LoadingContext";
import { CameraIcon } from "../components/icons";

// Preview circle size in px. Used by both the rendered container (via
// inline style, since Tailwind's arbitrary-size classes don't get picked
// up by JIT when computed from a variable) AND the crop math below.
// Keep these in lockstep — if you change one, change the other.
const PREVIEW_SIZE = 144;

function Register() {
  const [formData, setFormData] = useState({
    name: "",
    ownerName: "",
    contactNumber: "",
    email: "",
    password: "",
    address: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  // Hidden <input type="file"> ref — the visible button delegates to it
  // via .click() so we can render a centered, themed upload control
  // instead of the browser's default file-input chrome.
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageError, setImageError] = useState("");
  const [imageZoom, setImageZoom] = useState(1);
  const { setLoading, setGlobalLoadingMessage } = useLoading();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    // For contactNumber, allow only digits and limit to 11 chars
    if (name === "contactNumber") {
      const digits = value.replace(/[^0-9]/g, "").slice(0, 11);
      setFormData({ ...formData, contactNumber: digits });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (1MB limit)
      if (file.size > 1024 * 1024) {
        setImageError("Image size must be less than 1MB for preview");
        setImagePreview(null);
        setSelectedFile(null);
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        setImageError("Only image files are allowed");
        setImagePreview(null);
        return;
      }

      setImageError("");
      const reader = new FileReader();
      reader.onloadend = (e) => {
        setImagePreview(e.target.result);
        setSelectedFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (!imagePreview) return;

    const image = new Image();
    image.src = imagePreview;
    image.onload = () => {
      const naturalWidth = image.width;
      const naturalHeight = image.height;
      const scale = Math.max(PREVIEW_SIZE / naturalWidth, PREVIEW_SIZE / naturalHeight);
      const width = naturalWidth * scale * imageZoom;
      const height = naturalHeight * scale * imageZoom;
      setImageSize({ width, height });
      setImageOffset({
        x: (PREVIEW_SIZE - width) / 2,
        y: (PREVIEW_SIZE - height) / 2,
      });
    };
  }, [imagePreview, imageZoom]);

  const getClampedOffset = (offsetX, offsetY, width, height, previewSize) => {
    let x = offsetX;
    let y = offsetY;
    if (width > previewSize) {
      x = Math.min(0, Math.max(previewSize - width, x));
    } else {
      x = (previewSize - width) / 2;
    }
    if (height > previewSize) {
      y = Math.min(0, Math.max(previewSize - height, y));
    } else {
      y = (previewSize - height) / 2;
    }
    return { x, y };
  };

  const handleImagePointerDown = (e) => {
    if (!imagePreview) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, originX: imageOffset.x, originY: imageOffset.y });
  };

  const handleImagePointerMove = (e) => {
    if (!isDragging || !dragStart) return;
    e.preventDefault();
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    const { x, y } = getClampedOffset(
      dragStart.originX + dx,
      dragStart.originY + dy,
      imageSize.width,
      imageSize.height,
      PREVIEW_SIZE
    );
    setImageOffset({ x, y });
  };

  const handleImagePointerUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleZoom = (direction) => {
    setImageZoom(prev => {
      const newZoom = direction === 'in' ? prev + 0.1 : prev - 0.1;
      return Math.max(1, Math.min(3, newZoom));
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setUploadProgress(0);
    setLoading(true);
    setGlobalLoadingMessage("Registering your bureau...");

    // Validate all inputs
    if (!sanitizeText(formData.name).trim()) {
      setError("Bureau name is required");
      setLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!sanitizeText(formData.ownerName).trim()) {
      setError("Owner name is required");
      setLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!validateEmail(formData.email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!validatePassword(formData.password).valid) {
      setError(validatePassword(formData.password).message);
      setLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!validatePhone(formData.contactNumber)) {
      setError("Please enter a valid phone number");
      setLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!sanitizeText(formData.address).trim()) {
      setError("Address is required");
      setLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    try {
      setUploadProgress(10);

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('name', sanitizeText(formData.name));
      submitData.append('ownerName', sanitizeText(formData.ownerName));
      submitData.append('contactNumber', sanitizeText(formData.contactNumber));
      submitData.append('email', sanitizeText(formData.email));
      submitData.append('password', sanitizeText(formData.password));
      submitData.append('address', sanitizeText(formData.address));

      // Add image if selected
      if (selectedFile) {
        const croppedBlob = await getCroppedImageBlob(selectedFile, imageSize, imageOffset, 200);
        submitData.append('image', croppedBlob, selectedFile.name);
      }

      const response = await apiFetch("/auth/register", {
        method: "POST",
        body: submitData,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(percentCompleted);
          }
        }
      });

      setUploadProgress(100);

      if (response.imageInfo) {
        setSuccess(`Registration successful! Image compressed from ${response.imageInfo.originalSize} to ${response.imageInfo.compressedSize}${response.imageInfo.compression}`);
      } else {
        setSuccess("Registration successful! Redirecting to login...");
      }

      setTimeout(() => navigate("/bureau"), 2000);
    } catch (err) {
      console.error("Register error:", err);
      setError(err.message || "Something went wrong. Please try again.");
      setUploadProgress(0);
    } finally {
      setLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const getCroppedImageBlob = (file, displaySize, offset, previewSize) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const url = URL.createObjectURL(file);
      image.onload = async () => {
        try {
          const scale = displaySize.width / image.width;
          const sourceX = Math.max(0, -offset.x) / scale;
          const sourceY = Math.max(0, -offset.y) / scale;
          const sourceWidth = previewSize / scale;
          const sourceHeight = previewSize / scale;

          const canvas = document.createElement('canvas');
          canvas.width = previewSize;
          canvas.height = previewSize;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            previewSize,
            previewSize
          );

          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create cropped image'));
            }
          }, file.type || 'image/jpeg');
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      image.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(new Error('Unable to load image for cropping'));
      };
      image.src = url;
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-background)]">
      <div className="w-full max-w-md rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8 shadow-xl shadow-[var(--color-primary-soft)]">
        <h2 className="mb-6 text-center text-2xl font-bold text-[var(--color-text-strong)]">
          Bureau Registration
        </h2>

        {error && (
          <div className="mb-4 rounded-xl border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-4 py-2 text-[var(--color-danger-text)]">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-xl border border-[var(--color-notice-border)] bg-[var(--color-notice-bg)] px-4 py-2 text-[var(--color-notice-text)]">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Profile Picture Upload — centered column with placeholder /
              preview circle + a single themed button. The raw file input is
              hidden and triggered via ref so the visible UI stays clean. */}
          <div className="mb-6 flex flex-col items-center text-center">
            <label className="mb-3 block text-sm font-medium text-[var(--color-text-muted)]">
              Profile Picture (Optional)
            </label>

            {/* Hidden native input — clicked via the button below. */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />

            {imagePreview ? (
              <>
                {/* Preview circle — drag to reposition, zoom buttons below.
                    Pixel size lives in PREVIEW_SIZE so it stays in sync
                    with the crop math above. */}
                <div
                  className="relative rounded-full overflow-hidden border-2 border-[var(--color-border)] bg-[var(--color-background)]"
                  style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
                >
                  <div
                    className="absolute inset-0 cursor-grab touch-none"
                    onPointerDown={handleImagePointerDown}
                    onPointerMove={handleImagePointerMove}
                    onPointerUp={handleImagePointerUp}
                    onPointerLeave={handleImagePointerUp}
                  >
                    <img
                      src={imagePreview}
                      alt="Profile preview"
                      className="absolute"
                      style={{
                        left: `${imageOffset.x}px`,
                        top: `${imageOffset.y}px`,
                        width: `${imageSize.width}px`,
                        height: `${imageSize.height}px`,
                      }}
                      draggable={false}
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 rounded-full border border-dashed border-[var(--color-border)]" />
                </div>
                <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
                  Drag image to position the crop area
                </p>

                {/* Zoom controls */}
                <div className="mt-2 flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => handleZoom("out")}
                    disabled={imageZoom <= 1}
                    className="px-3 py-1 rounded-lg border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    −
                  </button>
                  <span className="flex items-center px-3 text-sm text-[var(--color-text-muted)]">
                    {(imageZoom * 100).toFixed(0)}%
                  </span>
                  <button
                    type="button"
                    onClick={() => handleZoom("in")}
                    disabled={imageZoom >= 3}
                    className="px-3 py-1 rounded-lg border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    +
                  </button>
                </div>

                {/* Change-photo action */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
                >
                  <CameraIcon className="h-4 w-4" />
                  Change photo
                </button>
              </>
            ) : (
              <>
                {/* Placeholder circle when no image has been chosen yet. */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
                  className="rounded-full border-2 border-dashed border-[var(--color-border)] bg-[var(--color-background)] flex flex-col items-center justify-center gap-2 text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
                  aria-label="Upload profile picture"
                >
                  <CameraIcon className="h-7 w-7" />
                  <span className="text-xs font-medium">
                    Tap to add photo
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
                >
                  <CameraIcon className="h-4 w-4" />
                  Upload photo
                </button>
              </>
            )}

            {imageError && (
              <p className="mt-2 text-sm text-red-500">{imageError}</p>
            )}

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-3 w-full">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}

            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              JPG, PNG formats. Max size: 1MB (larger files will be compressed)
            </p>
          </div>

          {/* Bureau Name */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Bureau Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              aria-required="true"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
            />
          </div>

          {/* Owner Name */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Owner Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleChange}
              required
              aria-required="true"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
            />
          </div>

          {/* Contact Number */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Contact Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              required
              aria-required="true"
              inputMode="numeric"
              pattern="[0-9]{11}"
              maxLength={11}
              autoCapitalize="off"
              autoCorrect="off"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
            />
            <p className="mt-2 text-xs text-[var(--color-text-muted)]">Enter exactly 11 digits (0-9).</p>
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              aria-required="true"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
            />
          </div>

          {/* Password with show/hide toggle */}
          <div className="relative mb-4">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              aria-required="true"
              minLength={8}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 pr-10 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-10 text-sm font-medium text-[var(--color-text)] transition hover:text-[var(--color-text-strong)]"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
            <p className="mt-2 text-xs text-[var(--color-text-muted)]">Password must be at least 8 characters.</p>
          </div>

          {/* Address */}
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              aria-required="true"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
            />
          </div>

          <button
            type="submit"
            className="w-full rounded-lg bg-[var(--color-primary)] py-2 text-sm font-semibold text-[var(--color-on-primary)] shadow-sm transition hover:bg-[var(--color-primary-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-soft)]"
          >
            Register
          </button>

          <p className="mt-4 text-center text-sm text-[var(--color-text)]">
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => navigate("/bureau")}
              className="font-semibold text-[var(--color-text)] transition hover:text-[var(--color-text-strong)]"
            >
              Login
            </button>
          </p>

         
        </form>
      </div>
    </div>
  );
}

export default Register;
